import React, { useCallback, useEffect, useRef, useState } from 'react';

const ProfileImageGlowing = ({ src, alt }) => {
  const containerRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);
  const [glowPosition, setGlowPosition] = useState({ angle: 0, active: false });
  const animationFrameRef = useRef(0);

  const handleMouseMove = useCallback((e) => {
    if (!containerRef.current) return;

    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }

    animationFrameRef.current = requestAnimationFrame(() => {
      const element = containerRef.current;
      if (!element) return;

      const { left, top, width, height } = element.getBoundingClientRect();
      const centerX = left + width / 2;
      const centerY = top + height / 2;
      
      // Check if mouse is within reasonable proximity
      const distance = Math.sqrt(
        Math.pow(e.clientX - centerX, 2) + Math.pow(e.clientY - centerY, 2)
      );
      
      // Only activate if within 200px of center
      if (distance < 200) {
        const angle = Math.atan2(e.clientY - centerY, e.clientX - centerX);
        const degrees = (angle * 180) / Math.PI + 90;
        setGlowPosition({ angle: degrees, active: true });
      } else {
        setGlowPosition(prev => ({ ...prev, active: false }));
      }
    });
  }, []);

  const handleMouseLeave = useCallback(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    setGlowPosition({ angle: 0, active: false });
    setIsHovered(false);
  }, []);

  const handleMouseEnter = useCallback(() => {
    setIsHovered(true);
  }, []);

  useEffect(() => {
    const handleGlobalMouseMove = (e) => {
      handleMouseMove(e);
    };

    if (isHovered) {
      window.addEventListener('mousemove', handleGlobalMouseMove);
      return () => {
        window.removeEventListener('mousemove', handleGlobalMouseMove);
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
        }
      };
    }
  }, [isHovered, handleMouseMove]);

  const wrapperStyle = {
    position: 'relative',
    width: '120px',
    height: '120px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  };

  const glowStyle = {
    position: 'absolute',
    inset: '-2px',
    borderRadius: '50%',
    background: `conic-gradient(
      from ${glowPosition.angle}deg at 50% 50%,
      transparent 0deg,
      #6366f1 20deg,
      #ec4899 40deg,
      #f59e0b 60deg,
      #10b981 80deg,
      #3b82f6 100deg,
      transparent 120deg,
      transparent 360deg
    )`,
    opacity: glowPosition.active ? 0.8 : 0,
    transition: 'opacity 0.3s ease',
    filter: 'blur(1px)',
  };

  const backgroundGlowStyle = {
    position: 'absolute',
    inset: '-15px',
    borderRadius: '50%',
    background: `radial-gradient(
      circle at 50% 50%,
      #6366f130 0%,
      transparent 70%
    )`,
    opacity: isHovered ? 0.5 : 0,
    transition: 'opacity 0.3s ease',
    filter: 'blur(15px)',
  };

  const borderContainerStyle = {
    position: 'absolute',
    inset: '-1px',
    borderRadius: '50%',
    background: `conic-gradient(
      from 0deg,
      #6366f1 0deg,
      #7c3aed 60deg,
      #ec4899 120deg,
      #f59e0b 180deg,
      #10b981 240deg,
      #3b82f6 300deg,
      #6366f1 360deg
    )`,
    opacity: isHovered ? 0.9 : 0.5,
    transition: 'all 0.3s ease',
    animation: isHovered ? 'spin 20s linear infinite' : 'rainbowCycle 8s ease-in-out infinite',
  };

  const innerCircleStyle = {
    position: 'absolute',
    inset: '1px',
    borderRadius: '50%',
    backgroundColor: 'var(--surface-color)',
    transition: 'all 0.3s ease',
  };

  const imageStyle = {
    width: '100px',
    height: '100px',
    borderRadius: '50%',
    objectFit: 'cover',
    position: 'relative',
    zIndex: 1,
    filter: isHovered ? 'brightness(1.05)' : 'brightness(1)',
    transform: isHovered ? 'scale(1.02)' : 'scale(1)',
    transition: 'all 0.3s ease',
  };

  return (
    <>
      <style jsx>{`
        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
        
        @keyframes rainbowCycle {
          0%, 100% {
            filter: hue-rotate(0deg) brightness(1);
            transform: rotate(0deg);
          }
          20% {
            filter: hue-rotate(72deg) brightness(1.1);
          }
          40% {
            filter: hue-rotate(144deg) brightness(1);
          }
          60% {
            filter: hue-rotate(216deg) brightness(1.1);
          }
          80% {
            filter: hue-rotate(288deg) brightness(1);
          }
        }
      `}</style>
      <div 
        ref={containerRef}
        style={wrapperStyle}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {/* Background glow */}
        <div style={backgroundGlowStyle} />
        
        {/* Directional glow */}
        <div style={glowStyle} />
        
        {/* Gradient border */}
        <div style={borderContainerStyle} />
        
        {/* Inner circle (creates the border effect) */}
        <div style={innerCircleStyle} />
        
        {/* Profile image */}
        <img
          src={src}
          alt={alt}
          loading="lazy"
          decoding="async"
          width={100}
          height={100}
          style={imageStyle}
        />
      </div>
    </>
  );
};

export default ProfileImageGlowing;
